/**
 * API Response Transformers
 * Converts Pascal case API responses to camel case application types
 */

import { MESSAGE_ROLE, Messages, MessageStatus, MessageType } from '../../Message/types/messages';
import {
  ApiMessage,
  ApiMessagesResponse,
  ApiPrivacySettingsResponse,
  OcPrivacySettings,
  OcSiteMessage,
  OcSiteMessagesResponse,
} from '../types/apiTypes';

/**
 * The like flag reaches us as the string "true" or "false": Nexus writes it into a
 * Dictionary<string, string> and both APIs pass that dictionary through untouched.
 */
const toMessageStatus = (liked: string | null | undefined): MessageStatus =>
  liked === 'true' ? true : liked === 'false' ? false : null;

/**
 * Transform API message to application message format
 * Converts Pascal case to camel case and normalizes types
 */
export const transformApiMessage = (msg: ApiMessage): MessageType => ({
  role: msg.Role.toLowerCase() as MESSAGE_ROLE,
  message: msg.Message,
  messageId: msg.MessageId,
  actionStates: {
    liked: toMessageStatus(msg.ActionStates?.Liked),
  },
  messageReferences: msg.MessageReferences?.map((ref) => ({
    title: ref.Title,
    href: ref.Url, // Map Url to href for compatibility
    url: ref.Url,
    itemType: ref.ItemType,
  })) || undefined,
});

/**
 * Transform API messages response to application Messages type
 * Converts Pascal case to camel case for the entire response
 */
export const transformApiMessagesResponse = (data: ApiMessagesResponse): Messages => ({
  sessionId: data.SessionId,
  messages: data.Messages.map(transformApiMessage),
});

/**
 * oc-api chat payloads are already camelCase (JsonCamelCaseResult + ChatResponse JsonProperty).
 * Normalize role casing to match Crest-transformed Messages.
 */
export const transformOcSiteMessage = (msg: OcSiteMessage): MessageType => ({
  role: msg.role.toLowerCase() as MESSAGE_ROLE,
  message: msg.message,
  messageId: msg.messageId,
  actionStates: {
    liked: toMessageStatus(msg.actionStates?.liked),
  },
  messageReferences: msg.messageReferences?.map((ref) => ({
    title: ref.title || '',
    href: ref.url || '',
    url: ref.url || '',
    itemType: ref.itemType || '',
  })),
});

export const transformOcSiteMessagesResponse = (data: OcSiteMessagesResponse): Messages => ({
  sessionId: data.sessionId,
  messages: (data.messages || []).map(transformOcSiteMessage),
});

/**
 * Transform GetPrivacySettings API response to application format
 * Converts Pascal case to camel case
 */
export const transformApiPrivacySettingsResponse = (
  data: ApiPrivacySettingsResponse,
): OcPrivacySettings => ({
  isPrivate: data.IsPrivate,
  isAnonymous: data.IsAnonymous,
  allowPublicAccess: data.AllowPublicAccess,
});

